import {
  cloneElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FiAlertCircle,
  FiPlus,
  FiSave,
  FiSearch,
  FiTrash2,
  FiTruck,
  FiUpload,
  FiX,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import PurchaseOrderProductFilterModal from "./components/PurchaseOrderProductFilterModal";
import { saveSheetGrns, sheetReceiptPayload } from "./grnSheetSave";
import AgreementField from './AgreementField';
import { ptInvoiceDate } from './ptInvoiceDate';

const numeric = (value) => Number(value || 0);
const optionalNumber = (value) =>
  value === "" || value === null || value === undefined
    ? undefined
    : numeric(value);
const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(numeric(value));

const productValue = (variant, purchaseOrderItem, key) =>
  variant.product?.[key] ?? purchaseOrderItem?.variant?.product?.[key];

const toItem = (variant, purchaseOrderItem = null) => ({
  purchaseOrderItemId: purchaseOrderItem?.id || null,
  variantId: variant.id || purchaseOrderItem?.variantId,
  product: productValue(variant, purchaseOrderItem, "name") || "Product",
  articleNumber: productValue(variant, purchaseOrderItem, "productCode") || "",
  brand: productValue(variant, purchaseOrderItem, "brand")?.name || "",
  category: productValue(variant, purchaseOrderItem, "category")?.name || "",
  originalGroupName: variant.pt?.originalGroupName || variant.attributes?.originalGroupName || "",
  subCategory:
    productValue(variant, purchaseOrderItem, "subCategory")?.name || "",
  season: productValue(variant, purchaseOrderItem, "season")?.name || "",
  unit: productValue(variant, purchaseOrderItem, "unit")?.name || "",
  hsnCode: productValue(variant, purchaseOrderItem, "hsnCode") || "",
  gstRule:
    productValue(variant, purchaseOrderItem, "gstRule")?.ruleGroup ||
    productValue(variant, purchaseOrderItem, "gstSlab")?.ruleGroup ||
    "",
  sku: variant.sku || purchaseOrderItem?.variant?.sku || "—",
  variantName:
    variant.variantName || purchaseOrderItem?.variant?.variantName || "",
  barcode: variant.barcode || purchaseOrderItem?.variant?.barcode || "",
  supplierBarcode:
    variant.supplierBarcode ||
    purchaseOrderItem?.variant?.supplierBarcode ||
    "",
  color: variant.color?.name || purchaseOrderItem?.variant?.color?.name || "—",
  size: variant.size?.name || purchaseOrderItem?.variant?.size?.name || "—",
  sizeGroup:
    variant.size?.sizeGroup?.name ||
    purchaseOrderItem?.variant?.size?.sizeGroup?.name ||
    "",
  gstBands: variant.gstBands || purchaseOrderItem?.variant?.gstBands || [],
  ordered: purchaseOrderItem
    ? numeric(purchaseOrderItem.quantityOrdered)
    : null,
  alreadyReceived: purchaseOrderItem
    ? numeric(purchaseOrderItem.quantityReceived)
    : 0,
  received: purchaseOrderItem
    ? Math.max(
        0,
        numeric(purchaseOrderItem.quantityOrdered) -
          numeric(purchaseOrderItem.quantityReceived),
      )
    : 1,
  accepted: purchaseOrderItem
    ? Math.max(
        0,
        numeric(purchaseOrderItem.quantityOrdered) -
          numeric(purchaseOrderItem.quantityReceived),
      )
    : 1,
  rejected: 0,
  unitCost: numeric(
    purchaseOrderItem?.unitPrice ??
      variant.purchasePrice ??
      variant.lastPurchasePrice ??
      variant.costPrice,
  ),
  mrp: optionalNumber(variant.mrp) ?? "",
  posPrice: optionalNumber(variant.posPrice ?? variant.mrp) ?? "",
  onlinePrice: optionalNumber(variant.onlinePrice) ?? "",
  cashDiscountPercent: numeric(variant.cashDiscountPercent),
  tradeDiscountPercent: numeric(variant.tradeDiscountPercent),
  specialDiscountPercent: numeric(variant.specialDiscountPercent),
  supplierTaxRate1: optionalNumber(variant.supplierTaxRate1) ?? "",
  supplierTaxRate3: optionalNumber(variant.supplierTaxRate3) ?? "",
  batchNumber: "",
  notes: "",
});

const resolvedTax = (bands, unitCost) => {
  const cost = numeric(unitCost);
  const match = (bands || []).find((band) => {
    const minimum =
      band.minimumItemValue == null ? null : numeric(band.minimumItemValue);
    const maximum =
      band.maximumItemValue == null ? null : numeric(band.maximumItemValue);
    return (
      (minimum === null || cost >= minimum) &&
      (maximum === null || cost <= maximum)
    );
  });
  return numeric(match?.rate);
};

const discountBreakdown = (item) => {
  const basicRate = numeric(item.unitCost);
  const afterCd = basicRate * (1 - numeric(item.cashDiscountPercent) / 100);
  const afterTd = afterCd * (1 - numeric(item.tradeDiscountPercent) / 100);
  const netRate = afterTd * (1 - numeric(item.specialDiscountPercent) / 100);
  return {
    cdAmount: basicRate - afterCd,
    tdAmount: afterCd - afterTd,
    spcdAmount: afterTd - netRate,
    netRate,
  };
};

const normalizeState = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

const inputClass =
  "h-10 w-20 rounded-lg border border-gray-200 bg-white px-2 text-center text-sm outline-none focus:border-primary-500";

export default function GRNCreatePage() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const ptFileInput = useRef(null);
  const sheetProgress = useRef({});
  const sheetSaveScope = useRef(null);
  const saveLock = useRef(false);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stores, setStores] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [purchaseOrderId, setPurchaseOrderId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [agreementType, setAgreementType] = useState('');
  const [storeId, setStoreId] = useState("");
  const [seasonId, setSeasonId] = useState("");
  const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState("");
  const [supplierInvoiceDate, setSupplierInvoiceDate] = useState("");
  const [receivedAt, setReceivedAt] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [freightAmount, setFreightAmount] = useState("");
  const [freightTaxRate, setFreightTaxRate] = useState("0");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [ptImporting, setPtImporting] = useState(false);
  const [ptImportJobId, setPtImportJobId] = useState(null);
  const [ptSummary, setPtSummary] = useState(null);
  const [ptSheetChoice, setPtSheetChoice] = useState(null);
  const [ptSelectedSheets, setPtSelectedSheets] = useState([]);
  const [ptSheets, setPtSheets] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isPoLinked = Boolean(purchaseOrderId);
  const selectedStoreName = useMemo(
    () => stores.find((store) => store.id === storeId)?.name || stores.find((store) => store.id === storeId)?.displayName || "—",
    [stores, storeId],
  );
  const sameStateSupply = useMemo(() => {
    const supplier = suppliers.find((item) => item.id === supplierId);
    const store = stores.find((item) => item.id === storeId);
    const supplierState = normalizeState(supplier?.state);
    const storeState = normalizeState(store?.state);
    return supplierState && storeState ? supplierState === storeState : null;
  }, [supplierId, storeId, suppliers, stores]);

  const loadOptions = useCallback(async () => {
    try {
      const [orders, supplierResponse, storeResponse, seasonResponse] =
        await Promise.all([
          apiRequest("/purchases/orders?take=100", { accessToken }),
          apiRequest("/suppliers?take=500", { accessToken }),
          apiRequest("/inventory/stores?take=500", { accessToken }),
          apiRequest("/seasons?activeOnly=true", { accessToken }),
        ]);
      setPurchaseOrders(
        Array.isArray(orders) ? orders : orders.items || orders.data || [],
      );
      setSuppliers(
        Array.isArray(supplierResponse)
          ? supplierResponse
          : supplierResponse.items || supplierResponse.data || [],
      );
      setStores(
        Array.isArray(storeResponse)
          ? storeResponse
          : storeResponse.items || storeResponse.data || [],
      );
      setSeasons(
        Array.isArray(seasonResponse)
          ? seasonResponse
          : seasonResponse.items || seasonResponse.data || [],
      );
    } catch (requestError) {
      setError(requestError.message || "Unable to load GRN options.");
    }
  }, [accessToken]);

  useEffect(() => {
    if (accessToken) loadOptions();
  }, [accessToken, loadOptions]);

  const selectPurchaseOrder = async (id) => {
    setError("");
    setPtSummary(null);
    setPtSheets([]);
    sheetProgress.current = {};
    sheetSaveScope.current = null;
    setPtImportJobId(null);
    setPurchaseOrderId(id);
    setAgreementType('');
    if (!id) {
      setItems([]);
      return;
    }
    try {
      const order = await apiRequest(`/purchases/orders/${id}`, {
        accessToken,
      });
      setSupplierId(order.supplierId || order.supplier?.id || "");
      setStoreId(order.storeId || order.store?.id || "");
      setSeasonId(order.seasonId || order.season?.id || "");
      setItems(
        (order.items || [])
          .filter(
            (item) =>
              numeric(item.quantityOrdered) > numeric(item.quantityReceived),
          )
          .map((item) => toItem(item.variant || {}, item)),
      );
    } catch (requestError) {
      setError(requestError.message || "Unable to load this purchase order.");
    }
  };

  const updateItem = (index, field, value) => {
    setItems((current) =>
      current.map((item, row) => {
        if (row !== index) return item;
        const next = { ...item, [field]: value };
        if (field === 'mrp' && item.catalogDraft) next.posPrice = value;
        if (field === "received")
          next.accepted = Math.max(0, numeric(value) - numeric(next.rejected));
        if (field === "rejected")
          next.accepted = Math.max(0, numeric(next.received) - numeric(value));
        if (
          field === "accepted" &&
          numeric(value) + numeric(next.rejected) > numeric(next.received)
        ) {
          next.rejected = Math.max(0, numeric(next.received) - numeric(value));
        }
        return next;
      }),
    );
  };

  const addProducts = (variants) => {
    setItems((current) => {
      const existing = new Set(current.map((item) => item.variantId));
      return [
        ...current,
        ...variants
          .filter((variant) => !existing.has(variant.id))
          .map((variant) => toItem(variant)),
      ];
    });
  };

  const previewPtFile = async (file, sheetNames = null) => {
    if (!file) return;
    setError("");
    setPtImporting(true);
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("supplierId", supplierId);
      if (storeId) body.append("storeId", storeId);
      if (sheetNames) body.append("sheetNames", JSON.stringify(sheetNames));
      const response = await apiRequest("/purchases/grns/import/pt-preview", {
        method: "POST",
        body,
        accessToken,
      });
      if (response.selectionRequired) {
        setPtSheetChoice({ file, sheets: response.sheets || [] });
        setPtSelectedSheets((response.sheets || []).map((sheet) => sheet.name));
        return;
      }
      setPtSheetChoice(null);
      setPtImportJobId(response.importJobId || null);
      const previews = response.sheetPreviews || [{ ...response, sheetName: response.summary?.sheets?.[0] || "Sheet1" }];
      sheetProgress.current = {};
      sheetSaveScope.current = null;
      setPtSheets(previews.map((preview, index) => ({
        key: preview.importJobId || preview.groupKey || preview.sheetName,
        sheetName: preview.sheetName,
        branchName: preview.branchName || "",
        seasonName: preview.seasonName || "",
        storeId: preview.storeId || (preview.branchName ? "" : storeId),
        seasonId: preview.seasonId || (preview.seasonName ? "" : seasonId),
        ptImportJobId: preview.importJobId,
        supplierId,
        supplierInvoiceNumber: preview.supplierInvoiceNumber || "",
        supplierInvoiceDate: ptInvoiceDate(preview),
        sourceInvoiceDates: (preview.summary?.dates || []).join(', '),
        receivedAt: preview.receivedAt || receivedAt,
        freightAmount: index === 0 ? freightAmount : "0",
        freightTaxRate: index === 0 ? freightTaxRate : "0",
      })));
      const importedItems = previews.flatMap(preview => (preview.items || []).map(item => ({
        ...item, ptSheetKey: preview.importJobId || preview.groupKey || preview.sheetName,
        importSeason: preview.seasonName || "",
      })));
      const selectedSeason = seasons.find((season) => season.id === seasonId);
      setItems(
        importedItems.map((item) => ({
          ...toItem(item),
          // PT preview returns `variantId` rather than an embedded `id`.
          // Preserve it so each workbook line saves against its own variant.
          variantId: item.variantId,
          catalogDraft: item.catalogDraft || null,
          ptSheetKey: item.ptSheetKey,
          received: numeric(item.received),
          accepted: numeric(item.accepted),
          rejected: numeric(item.rejected),
          unitCost: numeric(item.unitCost),
          mrp: optionalNumber(item.mrp) ?? "",
          posPrice: optionalNumber(item.posPrice) ?? "",
          onlinePrice: optionalNumber(item.onlinePrice) ?? "",
          cashDiscountPercent: numeric(item.cashDiscountPercent),
          tradeDiscountPercent: numeric(item.tradeDiscountPercent),
          specialDiscountPercent: numeric(item.specialDiscountPercent),
          supplierTaxRate1: optionalNumber(item.supplierTaxRate1) ?? "",
          supplierTaxRate3: optionalNumber(item.supplierTaxRate3) ?? "",
          batchNumber: item.batchNumber || "",
          season: item.importSeason || selectedSeason?.name || "",
        })),
      );
      setSupplierInvoiceNumber(response.supplierInvoiceNumber || "");
      setSupplierInvoiceDate(response.supplierInvoiceDate || "");
      setReceivedAt(
        response.receivedAt || new Date().toISOString().slice(0, 10),
      );
      setPtSummary(response.summary || null);
    } catch (requestError) {
      setError(requestError.message || "PT workbook import failed.");
    } finally {
      setPtImporting(false);
    }
  };

  const importPtFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!supplierId) {
      setError("Select a supplier before importing a PT workbook.");
      return;
    }
    await previewPtFile(file);
  };

  const confirmPtSheets = async () => {
    if (!ptSheetChoice?.file || !ptSelectedSheets.length) {
      setError("Select at least one worksheet to import.");
      return;
    }
    await previewPtFile(ptSheetChoice.file, ptSelectedSheets);
  };

  const updateSheet = (key, field, value) => setPtSheets(current => current.map(sheet =>
    sheet.key === key ? { ...sheet, [field]: value, ...(field === 'supplierId' ? { agreementType: '' } : {}) } : sheet));
  const gridGroups = ptSheets.length ? ptSheets.map(sheet => {
    const supplier = suppliers.find(value => value.id === sheet.supplierId);
    const store = stores.find(value => value.id === sheet.storeId);
    const supplierState = normalizeState(supplier?.state);
    const storeState = normalizeState(store?.state);
    return { ...sheet, items: items.filter(item => item.ptSheetKey === sheet.key),
      sameStateSupply: supplierState && storeState ? supplierState === storeState : null };
  }) : [{ key: 'manual', items, sameStateSupply }];

  const totals = useMemo(() => {
    const goodsValue = items.reduce(
      (sum, item) =>
        sum + numeric(item.accepted) * discountBreakdown(item).netRate,
      0,
    );
    const goodsTax = items.reduce((sum, item) => {
      const discount = discountBreakdown(item);
      return (
        sum +
        numeric(item.accepted) *
          discount.netRate *
          (resolvedTax(item.gstBands, discount.netRate) / 100)
      );
    }, 0);
    const freight = ptSheets.length ? ptSheets.reduce((sum, sheet) => sum + numeric(sheet.freightAmount), 0) : numeric(freightAmount);
    const freightTax = ptSheets.length ? ptSheets.reduce((sum, sheet) => sum + numeric(sheet.freightAmount) * numeric(sheet.freightTaxRate) / 100, 0) : freight * (numeric(freightTaxRate) / 100);
    const exactTotal = goodsValue + goodsTax + freight + freightTax;
    const total = ptSheets.length ? ptSheets.reduce((sum, sheet) => {
      const goods = items.filter(item => item.ptSheetKey === sheet.key).reduce((value, item) => {
        const net = discountBreakdown(item).netRate;
        return value + numeric(item.accepted) * net * (1 + resolvedTax(item.gstBands, net) / 100);
      }, 0);
      return sum + Math.floor(goods + numeric(sheet.freightAmount) * (1 + numeric(sheet.freightTaxRate) / 100) + 0.5);
    }, 0) : Math.floor(exactTotal + 0.5);
    return {
      accepted: items.reduce((sum, item) => sum + numeric(item.accepted), 0),
      goodsValue,
      goodsTax,
      freight,
      freightTax,
      exactTotal,
      roundOffAmount: Number((total - exactTotal).toFixed(2)),
      total,
    };
  }, [freightAmount, freightTaxRate, items, ptSheets]);

  const save = async (postImmediately) => {
    if (saveLock.current) return;
    setError("");
    if (!supplierId || (!ptSheets.length && (!storeId || !seasonId)) || !items.length) {
      setError(
        "Select a supplier, receiving store, season, and at least one GRN line.",
      );
      return;
    }
    if (
      items.some(
        (item) =>
          numeric(item.received) <= 0 ||
          numeric(item.accepted) + numeric(item.rejected) !==
            numeric(item.received),
      )
    ) {
      setError(
        "For every line, accepted quantity plus rejected quantity must equal received quantity.",
      );
      return;
    }
    if (ptSheets.length && gridGroups.some(group => !group.supplierId || !group.storeId || !group.seasonId || !group.items.length)) {
      setError("Each imported group needs a supplier, receiving store, season and at least one line.");
      return;
    }
    if (sheetSaveScope.current && (sheetSaveScope.current.storeId !== storeId || sheetSaveScope.current.seasonId !== seasonId)) {
      setError("Some worksheet GRNs are already saved. Restore the original receiving store and season before retrying.");
      return;
    }
    saveLock.current = true;
    setSubmitting(true);
    try {
      const payload = {
        agreementType: agreementType || undefined,
        ...(ptImportJobId ? { ptImportJobId } : {}),
        ...(purchaseOrderId ? { purchaseOrderId } : { supplierId, storeId }),
        seasonId,
        supplierInvoiceNumber: supplierInvoiceNumber.trim() || undefined,
        supplierInvoiceDate: supplierInvoiceDate || undefined,
        receivedAt: receivedAt || undefined,
        freightAmount: numeric(freightAmount),
        freightTaxRate: numeric(freightTaxRate),
        notes: notes.trim() || undefined,
        items: items.map((item) => ({
          ...(item.purchaseOrderItemId
            ? { purchaseOrderItemId: item.purchaseOrderItemId }
            : {}),
          variantId: item.variantId,
          ...(item.catalogDraft ? { catalogDraft: item.catalogDraft } : {}),
          quantityReceived: numeric(item.received),
          quantityAccepted: numeric(item.accepted),
          quantityRejected: numeric(item.rejected),
          unitCost: numeric(item.unitCost),
          taxRate: resolvedTax(item.gstBands, discountBreakdown(item).netRate),
          cashDiscountPercent: numeric(item.cashDiscountPercent),
          tradeDiscountPercent: numeric(item.tradeDiscountPercent),
          specialDiscountPercent: numeric(item.specialDiscountPercent),
          supplierTaxRate1: optionalNumber(item.supplierTaxRate1),
          supplierTaxRate3: optionalNumber(item.supplierTaxRate3),
          mrp: optionalNumber(item.mrp),
          posPrice: optionalNumber(item.posPrice),
          onlinePrice: optionalNumber(item.onlinePrice),
          batchNumber: item.batchNumber.trim() || undefined,
          notes: item.notes.trim() || undefined,
        })),
      };
      // Check every worksheet before creating the first receipt.
      for (const group of ptSheets.length ? gridGroups : [null]) {
        const body = group ? sheetReceiptPayload(payload, group, items) : payload;
        const priceCheck = await apiRequest('/purchases/grns/check-online-prices', {
          method: 'POST', body, accessToken,
        });
        (group ? group.items : items).forEach((item, index) => {
          payload.items[items.indexOf(item)].onlinePrice = priceCheck.onlinePrices[index] ?? undefined;
        });
      }
      if (ptSheets.length) {
        await saveSheetGrns({
          groups: gridGroups, payload, allItems: items, progress: sheetProgress.current, postImmediately,
          create: body => apiRequest('/purchases/grns', { method: 'POST', body, accessToken }),
          post: id => apiRequest(`/purchases/grns/${id}/post`, { method: 'POST', accessToken }),
          onSaved: (key, progress) => {
            sheetSaveScope.current = { storeId, seasonId };
            updateSheet(key, 'savedGrn', progress.number);
          },
        });
        navigate("/admindashboard/purchases/grn");
        return;
      }
      const grn = await apiRequest("/purchases/grns", {
        method: "POST",
        body: payload,
        accessToken,
      });
      if (postImmediately)
        await apiRequest(`/purchases/grns/${grn.id}/post`, {
          method: "POST",
          accessToken,
        });
      navigate(`/admindashboard/purchases/grn/${grn.id}`);
    } catch (requestError) {
      const saved = Object.values(sheetProgress.current).map(value => value.number);
      setError((requestError.message || "Unable to create the goods receipt.") +
        (saved.length ? ` Already saved: ${saved.join(', ')}. Retry will skip these drafts and resume any pending posting. Do not reload this page.` : ""));
    } finally {
      setSubmitting(false);
      saveLock.current = false;
    }
  };

  return (
    <div className="mx-auto">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-700">
            Purchases
          </p>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Create goods receipt
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            PT data maps Article No to the product code and Brand to the Brand
            master. All commercial values are visible before you save.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
      </div>
      {error && (
        <div className="mb-6 flex gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      <section className="mb-6 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary-50 text-primary-700">
            <FiTruck />
          </span>
          <div>
            <h2 className="font-semibold text-gray-900">Receipt details</h2>
            <p className="text-sm text-gray-500">
              {ptSheets.length ? 'Review receipt and invoice details inside each imported group below.' : 'A purchase order is optional. Freight belongs to this GRN.'}
            </p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Field label="Purchase order (optional)" hint={ptSheets.length ? 'PT groups are direct GRNs. PO-linked receiving uses the manual flow.' : ''}>
            <select
              disabled={Boolean(ptSheets.length)}
              value={purchaseOrderId}
              onChange={(event) => selectPurchaseOrder(event.target.value)}
              className="field"
            >
              <option value="">Direct GRN — no purchase order</option>
              {purchaseOrders.map((order) => (
                <option key={order.id} value={order.id}>
                  {order.purchaseOrderNumber || order.id}
                </option>
              ))}
            </select>
          </Field>
          {!ptSheets.length && <>
          <AgreementField supplierId={supplierId} items={items} purchaseOrderId={purchaseOrderId} value={agreementType} onChange={setAgreementType} />
          <Field label="Supplier">
            <select
              disabled={isPoLinked}
              value={supplierId}
              onChange={(event) => { setSupplierId(event.target.value); setAgreementType(''); }}
              className="field disabled:bg-gray-50"
            >
              <option value="">Select supplier</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.displayName || supplier.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Receiving store">
            <select
              disabled={isPoLinked}
              value={storeId}
              onChange={(event) => setStoreId(event.target.value)}
              className="field disabled:bg-gray-50"
            >
              <option value="">Select store</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Season *">
            <select
              value={seasonId}
              onChange={(event) => setSeasonId(event.target.value)}
              className="field"
            >
              <option value="">Select season</option>
              {seasons.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.name} ({season.code})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Received date">
            <input
              disabled={Boolean(ptSheets.length)}
              type="date"
              value={receivedAt}
              onChange={(event) => setReceivedAt(event.target.value)}
              className="field"
            />
          </Field>
          <Field label="Supplier invoice no.">
            <input
              disabled={Boolean(ptSheets.length)}
              value={supplierInvoiceNumber}
              onChange={(event) => setSupplierInvoiceNumber(event.target.value)}
              className="field"
            />
          </Field>
          <Field label="Supplier invoice date">
            <input
              disabled={Boolean(ptSheets.length)}
              type="date"
              value={supplierInvoiceDate}
              onChange={(event) => setSupplierInvoiceDate(event.target.value)}
              className="field"
            />
          </Field>
          <Field label="Freight excl. GST">
            <input
              disabled={Boolean(ptSheets.length)}
              type="number"
              min="0"
              step="0.01"
              value={freightAmount}
              onChange={(event) => setFreightAmount(event.target.value)}
              className="field"
              placeholder="0.00"
            />
          </Field>
          <Field label="Freight GST">
            <select
              disabled={Boolean(ptSheets.length)}
              value={freightTaxRate}
              onChange={(event) => setFreightTaxRate(event.target.value)}
              className="field"
            >
              <option value="0">No GST</option>
              <option value="5">5%</option>
              <option value="12">12%</option>
              <option value="18">18%</option>
              <option value="28">28%</option>
            </select>
          </Field>
          </>}
          <Field label="Notes" extraClass="md:col-span-2">
            <input
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              className="field"
              placeholder="Optional receipt note"
            />
          </Field>
        </div>
      </section>

      <section className="mb-6 overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-gray-100 p-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              GRN transaction grid
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Scroll sideways to review every supplier, master-data, pricing,
              discount, tax, lot, and barcode value in one table.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <input
              ref={ptFileInput}
              type="file"
              accept=".xlsx,.xls"
              onChange={importPtFile}
              className="hidden"
            />
            <button
              type="button"
              disabled={!supplierId || isPoLinked || ptImporting || submitting || ptSheets.some(sheet => sheet.savedGrn)}
              onClick={() => ptFileInput.current?.click()}
              className="flex h-11 items-center gap-2 rounded-xl border border-primary-200 bg-primary-50 px-4 text-sm font-medium text-primary-800 disabled:opacity-50"
            >
              <FiUpload />
              {ptImporting ? "Reading PT file…" : "Import PT Excel"}
            </button>
            <button
              type="button"
              disabled={!supplierId || !storeId || isPoLinked || Boolean(ptSheets.length)}
              onClick={() => setProductModalOpen(true)}
              className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-4 text-sm font-medium text-white disabled:opacity-50"
            >
              <FiSearch />
              Find products
            </button>
          </div>
        </div>
        {ptSummary && (
          <div className="mx-6 mt-5 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            PT import ready: <strong>{ptSummary.importedLines}</strong> GRN
            lines from <strong>{ptSummary.workbookRows}</strong> rows. It matched
            <strong> {ptSummary.matchedVariants || 0}</strong> existing variants
            and staged <strong> {ptSummary.newVariantsToCreateOnSave || 0}</strong>{" "}
            new variants. New products, variants, and masters are not created
            until you save this GRN. Article No, Brand, Category, HSN/GST, size,
            supplier/EAN code, CD, TD, and SPCD were mapped where present.
            GSTTYPE from Excel selects the rule: GST APPAREL uses price bands;
            percentage values use fixed-rate GST. Missing HSN codes and fixed
            GST rules are created only on Save.
            Each worksheet / branch / season combination creates a separate GRN.
            Review its store, season, supplier, invoice dates and freight below.
            The initial freight is assigned only to the first group.
            {Boolean(ptSummary.skippedEmptySheets?.length) && ` Empty sheets skipped: ${ptSummary.skippedEmptySheets.join(', ')}.`}
          </div>
        )}
        {supplierId && storeId && sameStateSupply === null && (
          <div className="mx-6 mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Add a State to both the selected supplier and receiving store. It is
            required to decide whether this GRN uses CGST + SGST or IGST.
          </div>
        )}
        {gridGroups.map(group => (
        <fieldset key={group.key} disabled={submitting || Boolean(group.savedGrn)} className="min-w-0 border-t border-gray-200">
          {group.sheetName && (
            <div className="bg-primary-50/60 p-6">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold text-primary-800">{group.sheetName}</h3>
                  <p className="text-sm text-gray-600">{group.items.length} lines · {group.branchName || "Selected store"} · {group.seasonName || "Selected season"} · separate GRN{group.savedGrn ? ` · Saved: ${group.savedGrn}` : ""}</p>
                </div>
                <button type="button" onClick={() => {
                  setPtSheets(current => current.filter(sheet => sheet.key !== group.key));
                  setItems(current => current.filter(item => item.ptSheetKey !== group.key));
                }} className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-700">Remove sheet</button>
              </div>
              <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
                <AgreementField supplierId={group.supplierId} items={group.items} value={group.agreementType || ''} onChange={value => updateSheet(group.key, 'agreementType', value)} disabled={Boolean(group.savedGrn)} />
                <Field label="Receiving store / branch"><select disabled={Boolean(group.savedGrn)} value={group.storeId} onChange={event => updateSheet(group.key, 'storeId', event.target.value)} className="field"><option value="">Select store for {group.branchName || 'this group'}</option>{stores.map(store => <option key={store.id} value={store.id}>{store.name}</option>)}</select></Field>
                <Field label="Season"><select disabled={Boolean(group.savedGrn)} value={group.seasonId} onChange={event => updateSheet(group.key, 'seasonId', event.target.value)} className="field"><option value="">Select season: {group.seasonName}</option>{seasons.map(season => <option key={season.id} value={season.id}>{season.name}</option>)}</select></Field>
                <Field label="Supplier">
                  <select value={group.supplierId} onChange={event => updateSheet(group.key, 'supplierId', event.target.value)} className="field">
                    <option value="">Select supplier</option>
                    {suppliers.map(supplier => <option key={supplier.id} value={supplier.id}>{supplier.displayName || supplier.name}</option>)}
                  </select>
                </Field>
                <Field label="Supplier invoice no."><input value={group.supplierInvoiceNumber} onChange={event => updateSheet(group.key, 'supplierInvoiceNumber', event.target.value)} className="field" /></Field>
                <Field label="Invoice date" hint={!group.supplierInvoiceDate ? `Excel DATE: ${group.sourceInvoiceDates || 'not provided'} — re-import with the updated backend or enter the invoice date.` : ''}><input type="date" value={group.supplierInvoiceDate} onChange={event => updateSheet(group.key, 'supplierInvoiceDate', event.target.value)} className="field" /></Field>
                <Field label="Received date"><input type="date" value={group.receivedAt} onChange={event => updateSheet(group.key, 'receivedAt', event.target.value)} className="field" /></Field>
                <Field label="Freight excl. GST"><input type="number" min="0" step="0.01" value={group.freightAmount} onChange={event => updateSheet(group.key, 'freightAmount', event.target.value)} className="field" /></Field>
                <Field label="Freight GST"><select value={group.freightTaxRate} onChange={event => updateSheet(group.key, 'freightTaxRate', event.target.value)} className="field">{[0, 5, 12, 18, 28].map(rate => <option key={rate} value={rate}>{rate}%</option>)}</select></Field>
              </div>
              {group.sameStateSupply === null && <p className="mt-3 text-sm text-amber-700">Set the sheet supplier and receiving store states to calculate CGST/SGST or IGST.</p>}
            </div>
          )}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[3000px] text-sm">
            <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-4">Article No</th>
                <th className="px-4 py-4">Product / variant</th>
                <th className="px-4 py-4">Brand</th>
                <th className="px-4 py-4">Category</th>
                <th className="px-4 py-4">Subcategory</th>
                <th className="px-4 py-4">Original PT group</th>
                <th className="px-4 py-4">Size group</th>
                <th className="px-4 py-4">Unit</th>
                <th className="px-4 py-4">Size</th>
                <th className="px-4 py-4">Supplier lot / batch</th>
                <th className="px-4 py-4">Received</th>
                <th className="px-4 py-4">Accepted</th>
                <th className="px-4 py-4">Rejected</th>
                <th className="px-4 py-4">Basic rate</th>
                <th className="px-4 py-4">MRP</th>
                <th className="px-4 py-4">POS price</th>
                <th className="px-4 py-4">Online price</th>
                <th className="px-4 py-4">C.D. %</th>
                <th className="px-4 py-4">T.D. %</th>
                <th className="px-4 py-4">Basic amount</th>
                <th className="px-4 py-4">Net rate</th>
                <th className="px-4 py-4">HSN</th>
                <th className="px-4 py-4">CGST</th>
                <th className="px-4 py-4">SGST</th>
                <th className="px-4 py-4">IGST</th>
                <th className="px-4 py-4">GST amount</th>
                <th className="px-4 py-4">Line total</th>
                <th className="px-4 py-4">Store / godown</th>
                <th className="px-4 py-4">Supplier / EAN</th>
                <th className="px-4 py-4">CHARMS barcode</th>
                <th className="px-4 py-4">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {group.items.length ? (
                group.items.map((item, index) => (
                  <GrnRow
                    key={`${item.variantId}-${index}`}
                    item={item}
                    index={items.indexOf(item)}
                    sameStateSupply={group.sameStateSupply}
                    storeLabel={ptSheets.length ? stores.find(store => store.id === group.storeId)?.name || group.branchName || '' : selectedStoreName}
                    updateItem={updateItem}
                    remove={() =>
                      setItems((current) =>
                        current.filter(value => value !== item),
                      )
                    }
                  />
                ))
              ) : (
                <tr>
                  <td
                    colSpan="33"
                    className="px-6 py-16 text-center text-gray-500"
                  >
                    {isPoLinked
                      ? "No remaining PO lines to receive."
                      : "Select supplier and store, then import the PT workbook or find product variants."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </fieldset>
        ))}
        <div className="flex flex-col gap-4 border-t border-gray-100 bg-gray-50/70 px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <FiPlus className="text-primary-700" />
            {totals.accepted.toFixed(3)} accepted units
          </div>
          <div className="grid grid-cols-2 gap-6 text-right text-sm sm:grid-cols-4">
            <div>
              <p className="text-gray-500">Goods + freight</p>
              <p className="mt-1 font-semibold text-gray-900">
                {money(totals.goodsValue + totals.freight)}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Input GST</p>
              <p className="mt-1 font-semibold text-gray-900">
                {money(totals.goodsTax + totals.freightTax)}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Round off</p>
              <p className="mt-1 font-semibold text-gray-900">
                {totals.roundOffAmount > 0 ? "+" : ""}
                {money(totals.roundOffAmount)}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Total</p>
              <p className="mt-1 text-lg font-bold text-primary-800">
                {money(totals.total)}
              </p>
            </div>
          </div>
        </div>
      </section>
      <div className="flex flex-wrap justify-end gap-3">
        <button
          type="button"
          disabled={submitting}
          onClick={() => save(false)}
          className="flex h-11 items-center gap-2 rounded-xl border border-primary-200 bg-white px-5 text-sm font-semibold text-primary-800 disabled:opacity-50"
        >
          <FiSave />
          Save draft
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={() => save(true)}
          className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 text-sm font-semibold text-white disabled:opacity-50"
        >
          <FiTruck />
          {submitting ? "Saving…" : "Save and post receipt"}
        </button>
      </div>
      <PurchaseOrderProductFilterModal
        open={productModalOpen}
        supplierId={supplierId}
        accessToken={accessToken}
        onClose={() => setProductModalOpen(false)}
        onAddItems={addProducts}
      />
      <PtSheetChooser
        selection={ptSheetChoice}
        selectedSheets={ptSelectedSheets}
        setSelectedSheets={setPtSelectedSheets}
        importing={ptImporting}
        onClose={() => setPtSheetChoice(null)}
        onConfirm={confirmPtSheets}
      />
    </div>
  );
}

function PtSheetChooser({ selection, selectedSheets, setSelectedSheets, importing, onClose, onConfirm }) {
  if (!selection) return null;
  const sheets = selection.sheets || [];
  const allSelected = sheets.length > 0 && selectedSheets.length === sheets.length;
  const toggle = (name) => setSelectedSheets((current) => current.includes(name) ? current.filter((value) => value !== name) : [...current, name]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/50 p-4">
      <div className="w-full max-w-xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5"><div><h2 className="text-xl font-bold text-gray-950">Choose PT worksheets</h2><p className="mt-1 text-sm text-gray-500">This workbook has multiple sheets. Import one, several, or every sheet. Products are still only staged until you save the GRN.</p></div><button type="button" onClick={onClose} disabled={importing} className="rounded-xl p-2 text-gray-500 hover:bg-gray-100"><FiX size={20} /></button></div>
        <div className="max-h-[52vh] space-y-2 overflow-y-auto p-6"><label className="flex cursor-pointer items-center justify-between rounded-2xl border border-primary-100 bg-primary-50 px-4 py-3"><span><span className="block font-semibold text-primary-900">Import all worksheets</span><span className="text-xs text-primary-700">Select every listed sheet</span></span><input type="checkbox" checked={allSelected} onChange={() => setSelectedSheets(allSelected ? [] : sheets.map((sheet) => sheet.name))} className="h-4 w-4 accent-primary-800" /></label>{sheets.map((sheet) => <label key={sheet.name} className="flex cursor-pointer items-center justify-between rounded-2xl border border-gray-200 px-4 py-3 hover:border-primary-200"><span><span className="block font-medium text-gray-900">{sheet.name}</span><span className="text-xs text-gray-500">{sheet.rowCount} detected row{sheet.rowCount === 1 ? "" : "s"}</span></span><input type="checkbox" checked={selectedSheets.includes(sheet.name)} onChange={() => toggle(sheet.name)} className="h-4 w-4 accent-primary-800" /></label>)}</div>
        <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-5"><button type="button" onClick={onClose} disabled={importing} className="h-11 rounded-xl border border-gray-200 px-4 text-sm font-semibold text-gray-700">Cancel</button><button type="button" onClick={onConfirm} disabled={!selectedSheets.length || importing} className="h-11 rounded-xl bg-primary-800 px-5 text-sm font-semibold text-white disabled:opacity-50">{importing ? "Preparing preview…" : `Preview ${selectedSheets.length === sheets.length ? "all sheets" : `${selectedSheets.length} sheet${selectedSheets.length === 1 ? "" : "s"}`}`}</button></div>
      </div>
    </div>
  );
}

function Field({ label, children, extraClass = "", hint = "" }) {
  return (
    <label className={`text-sm font-medium text-gray-700 ${extraClass}`}>
      {label}
      {cloneElement(children, {
        className: `mt-1.5 h-11 w-full rounded-xl border border-gray-200 bg-white px-3 font-normal outline-none focus:border-primary-500 ${children.props.className || ""}`,
      })}
      {hint && <span className="mt-1 block text-xs font-normal text-gray-500">{hint}</span>}
    </label>
  );
}

function GrnRow({ item, index, sameStateSupply, storeLabel, updateItem, remove }) {
  const discount = discountBreakdown(item);
  const taxRate = resolvedTax(item.gstBands, discount.netRate);
  const cgstRate = sameStateSupply === true ? taxRate / 2 : 0;
  const sgstRate = sameStateSupply === true ? taxRate / 2 : 0;
  const igstRate = sameStateSupply === false ? taxRate : 0;
  const basicAmount = numeric(item.accepted) * numeric(item.unitCost);
  const taxableAmount = numeric(item.accepted) * discount.netRate;
  const gstAmount = taxableAmount * (taxRate / 100);
  const lineTotal = taxableAmount + gstAmount;
  const text = (value, className = "") => (
    <td className={`px-4 py-4 align-top text-xs text-gray-700 ${className}`}>
      {value || "—"}
    </td>
  );
  const editable = (field, options = {}) => (
    <td className="px-4 py-4 align-top">
      <input
        type="number"
        min="0"
        step={options.step || "0.01"}
        value={item[field]}
        onChange={(event) => updateItem(index, field, event.target.value)}
        className={inputClass}
      />
    </td>
  );
  return (
    <tr className="border-t border-gray-100 align-top hover:bg-gray-50/70">
      {text(item.articleNumber, "font-mono")}
      <td className="px-4 py-4">
        <p className="font-semibold text-gray-900">{item.product}</p>
        <p className="mt-1 text-xs text-gray-500">
          {item.variantName || "Default variant"}
        </p>
        {item.ordered != null && (
          <p className="mt-1 text-xs text-primary-700">
            PO: {item.ordered} ordered · {item.alreadyReceived} received
          </p>
        )}
      </td>
      {text(item.brand)}
      {text(item.category)}
      {text(item.subCategory)}
      {text(item.originalGroupName)}
      {text(item.sizeGroup)}
      {text(item.unit)}
      {text(item.size || item.variantName)}
      <td className="px-4 py-4 align-top">
        <input
          value={item.batchNumber}
          onChange={(event) =>
            updateItem(index, "batchNumber", event.target.value)
          }
          className="h-10 w-28 rounded-lg border border-gray-200 px-2 text-sm"
          placeholder="Optional"
        />
      </td>
      {editable("received", { step: "0.001" })}
      {editable("accepted", { step: "0.001" })}
      {editable("rejected", { step: "0.001" })}
      {editable("unitCost")}
      {editable("mrp")}
      {item.catalogDraft ? text(money(item.mrp)) : editable("posPrice")}
      {editable("onlinePrice")}
      {editable("cashDiscountPercent")}
      {editable("tradeDiscountPercent")}
      {text(money(basicAmount), "font-semibold")}
      {text(money(discount.netRate), "font-semibold text-primary-800")}
      {text(item.hsnCode, "font-mono")}
      {text(
        sameStateSupply === null ? "Set states" : `${cgstRate.toFixed(2)}%`,
      )}
      {text(
        sameStateSupply === null ? "Set states" : `${sgstRate.toFixed(2)}%`,
      )}
      {text(
        sameStateSupply === null ? "Set states" : `${igstRate.toFixed(2)}%`,
      )}
      {text(money(gstAmount), "font-semibold text-primary-800")}
      {text(money(lineTotal), "font-bold text-gray-900")}
      {text(storeLabel)}
      {text(item.supplierBarcode, "font-mono")}
      {text(item.barcode, "font-mono")}
      <td className="px-4 py-4 align-top"><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${!item.variantId ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{!item.variantId ? "New on save" : "Matched"}</span></td>
      <td className="px-4 py-4">
        <button
          type="button"
          onClick={remove}
          className="rounded-lg p-2 text-red-600 hover:bg-red-50"
          aria-label="Remove GRN line"
        >
          <FiTrash2 />
        </button>
      </td>
    </tr>
  );
}
